import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';

const RecordTestPageNewFixed = () => {
  const navigate = useNavigate();
  const { sessionId } = useParams();

  // API基础URL - 直接指向backend-workers
  const API_BASE_URL = process.env.REACT_APP_WORKERS_API_URL || 'http://localhost:3002';

  // 健康状态
  const [healthStatus, setHealthStatus] = useState('checking');
  const [healthError, setHealthError] = useState(null);

  // 简化的状态
  const [isRecording, setIsRecording] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [isStarting, setIsStarting] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [segments, setSegments] = useState([]);
  const [error, setError] = useState('');
  const [audioUrl, setAudioUrl] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isAudioLoading, setIsAudioLoading] = useState(false);
  const [uploadInterval] = useState(5); // 固定5秒间隔
  const [isHistoricalSession, setIsHistoricalSession] = useState(false);
  const [isStopping, setIsStopping] = useState(false);
  const [copySuccess, setCopySuccess] = useState(false);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [transcriptionResult, setTranscriptionResult] = useState(null);

  // 缓存管理配置 - 简化配置
  const [cacheConfig] = useState({
    autoCleanup: true, // 自动清理已完成的segment
    cleanupDelay: 30000, // 清理延迟30秒
    maxCacheSize: parseInt(process.env.REACT_APP_CACHE_MAX_SIZE) || 5, // 最大缓存数量
    cleanupOnCompleted: true // 转写完成后立即清理
  });

  // 录音器相关 - 完全基于 audio-segment-recorder.html 的实现
  const mediaRecorderRef = useRef(null);
  const streamRef = useRef(null);
  const timerRef = useRef(null);
  const splitTimerRef = useRef(null);
  const segmentIndexRef = useRef(1); // 从1开始，与参考文件一致
  const audioChunksRef = useRef([]); // 当前录音片段的数据
  const audioIdRef = useRef(null);
  const isRecordingRef = useRef(false);
  const audioRef = useRef(null);
  const segmentStartTimeRef = useRef(null);
  const recordingStartTimeRef = useRef(null);

  // 设备管理相关
  const [availableDevices, setAvailableDevices] = useState([]);
  const [selectedDeviceId, setSelectedDeviceId] = useState('');
  const [showDeviceSelector, setShowDeviceSelector] = useState(false);

  // 本地音频缓存 - 用于播放
  const audioSegmentsRef = useRef(new Map()); // segmentIndex -> {audioBlob, audioUrl}
  const cacheCleanupTimersRef = useRef({}); // segmentIndex -> timerId
  const currentlyPlayingRef = useRef(null); // 当前正在播放的segmentIndex

  // 任务队列管理
  const taskQueueRef = useRef([]);
  const isProcessingQueueRef = useRef(false);
  const failedTasksRef = useRef({});
  const retryTimersRef = useRef({});

  // 格式化时间显示
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // 获取可用的音频设备
  const getAudioDevices = async () => {
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      const audioInputs = devices.filter(device => device.kind === 'audioinput');
      console.log('🎤 检测到音频输入设备:', audioInputs);
      setAvailableDevices(audioInputs);
      return audioInputs;
    } catch (error) {
      console.error('❌ 获取音频设备失败:', error);
      return [];
    }
  };

  // 选择录音设备
  const selectDevice = async (deviceId) => {
    console.log(`🎯 选择录音设备: ${deviceId}`);
    setSelectedDeviceId(deviceId);
    setShowDeviceSelector(false);
  };

  // 监听设备变化
  useEffect(() => {
    const handleDeviceChange = () => {
      console.log('🔄 音频设备发生变化');
      getAudioDevices();
    };

    navigator.mediaDevices.addEventListener('devicechange', handleDeviceChange);

    // 初始获取设备列表
    getAudioDevices();

    return () => {
      navigator.mediaDevices.removeEventListener('devicechange', handleDeviceChange);
    };
  }, []);

  // 缓存管理函数
  const cleanupSegmentCache = (segmentIndex, immediate = false) => {
    const segmentData = audioSegmentsRef.current.get(segmentIndex);
    if (!segmentData) return;

    console.log(`🧹 清理segment ${segmentIndex} 的本地缓存 ${immediate ? '(立即)' : '(延迟)'}`);

    // 清理延迟定时器（如果存在）
    if (cacheCleanupTimersRef.current[segmentIndex]) {
      clearTimeout(cacheCleanupTimersRef.current[segmentIndex]);
      delete cacheCleanupTimersRef.current[segmentIndex];
    }

    const performCleanup = () => {
      // 检查是否正在播放
      if (currentlyPlayingRef.current === segmentIndex) {
        console.log(`⏸️ segment ${segmentIndex} 正在播放，延迟清理`);
        // 重新安排清理
        cacheCleanupTimersRef.current[segmentIndex] = setTimeout(performCleanup, cacheConfig.cleanupDelay);
        return;
      }

      const segmentData = audioSegmentsRef.current.get(segmentIndex);
      if (segmentData) {
        // 释放Blob URL
        if (segmentData.audioUrl) {
          URL.revokeObjectURL(segmentData.audioUrl);
          console.log(`🗑️ 已释放segment ${segmentIndex}的Blob URL`);
        }

        // 从缓存中移除
        audioSegmentsRef.current.delete(segmentIndex);

        // 更新UI状态
        setSegments(prev => prev.map(seg =>
          seg.index === segmentIndex
            ? { ...seg, hasLocalAudio: false, localAudioUrl: null }
            : seg
        ));

        console.log(`✅ segment ${segmentIndex} 缓存清理完成`);
      }
    };

    if (immediate) {
      performCleanup();
    } else {
      // 延迟清理
      cacheCleanupTimersRef.current[segmentIndex] = setTimeout(performCleanup, cacheConfig.cleanupDelay);
    }
  };

  const scheduleCacheCleanup = (segmentIndex) => {
    if (!cacheConfig.autoCleanup) return;

    // 检查是否需要立即清理
    if (cacheConfig.cleanupOnCompleted) {
      cleanupSegmentCache(segmentIndex, false); // 延迟清理
    }
  };

  const enforceMaxCacheSize = () => {
    if (audioSegmentsRef.current.size <= cacheConfig.maxCacheSize) return;

    console.log(`📊 缓存超限 (${audioSegmentsRef.current.size}/${cacheConfig.maxCacheSize})，清理最旧的缓存`);

    // 获取所有segment索引并排序
    const segmentIndices = Array.from(audioSegmentsRef.current.keys()).sort((a, b) => a - b);
    const toRemove = segmentIndices.slice(0, segmentIndices.length - cacheConfig.maxCacheSize);

    toRemove.forEach(index => {
      cleanupSegmentCache(index, true); // 立即清理
    });
  };

  // 复制录音会话ID到剪贴板
  const copySessionId = async () => {
    const audioId = audioIdRef.current || sessionId;
    if (!audioId) return;

    try {
      await navigator.clipboard.writeText(audioId);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    } catch (err) {
      console.error('复制失败:', err);
    }
  };

  // 片段状态枚举
  const SegmentStatus = {
    PENDING: 'pending',
    UPLOADING: 'uploading',
    UPLOADED: 'uploaded',
    TRANSCRIBING: 'transcribing',
    COMPLETED: 'completed',
    FAILED: 'failed',
    RETRYING: 'retrying'
  };

  // 生成录音会话ID
  const generateAudioId = () => {
    return uuidv4();
  };

  // 新的上传和转写函数
  const uploadAndTranscribe = async (audioBlob, audioId, segmentIndex, duration) => {
    console.log(`🚀 开始上传和转写片段 ${segmentIndex}`);

    const formData = new FormData();
    formData.append('segment', audioBlob, `segment_${segmentIndex}.webm`);
    formData.append('audio_id', audioId);
    formData.append('segment_index', segmentIndex.toString());
    formData.append('timestamp', new Date().toISOString());

    const response = await fetch(`${API_BASE_URL}/api/upload_and_transcribe`, {
      method: 'POST',
      body: formData
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || `上传失败: ${response.status}`);
    }

    const result = await response.json();

    // 处理新的返回格式
    if (result.success) {
      console.log(`✅ 片段 ${segmentIndex} 处理成功`);
      console.log(`📤 上传状态: ${result.segment_uploaded ? '已上传' : '未上传'}`);
      console.log(`📝 转写状态: ${result.transcription_completed ? '已完成' : '仅上传'}`);

      if (result.transcription_text) {
        console.log(`📄 转写内容: "${result.transcription_text.substring(0, 100)}${result.transcription_text.length > 100 ? '...' : ''}"`);
      }
    } else {
      console.log(`❌ 片段 ${segmentIndex} 处理失败: ${result.error}`);

      // 处理特殊的错误情况
      if (result.transcription_successful && result.transcription_text) {
        console.log(`💡 特殊情况：转写成功但JSON保存失败`);
        console.log(`📄 转写内容仍可用: "${result.transcription_text.substring(0, 100)}${result.transcription_text.length > 100 ? '...' : ''}"`);
      }

      if (result.qwen_error) {
        console.log(`🤖 千问错误详情: ${result.qwen_error}`);
      }

      if (result.qwen_response) {
        console.log(`🔍 千问完整响应:`, result.qwen_response);
      }
    }

    return result;
  };

  // 查询单个segment状态
  const querySegmentStatus = async (audioId, segmentIndex) => {
    console.log(`🔍 [前端] 开始查询segment状态: audio_id=${audioId}, segment_index=${segmentIndex}`);

    const url = `${API_BASE_URL}/api/segment/${audioId}/${segmentIndex}/status`;
    console.log(`🔍 [前端] 请求URL: ${url}`);

    try {
      const response = await fetch(url);
      console.log(`🔍 [前端] 响应状态: ${response.status} ${response.statusText}`);

      if (!response.ok) {
        const errorText = await response.text();
        console.error(`❌ [前端] segment状态查询失败: ${response.status} - ${errorText}`);
        throw new Error(`查询失败: ${response.status}`);
      }

      const result = await response.json();
      console.log(`✅ [前端] segment状态查询成功:`, result);
      return result;
    } catch (error) {
      console.error(`❌ [前端] segment状态查询异常:`, error);
      throw error;
    }
  };

  // 查询整个audio会话状态
  const queryAudioStatus = async (audioId) => {
    console.log(`🔍 [前端] 开始查询audio状态: audio_id=${audioId}`);

    const url = `${API_BASE_URL}/api/audio/${audioId}/status`;
    console.log(`🔍 [前端] 请求URL: ${url}`);

    try {
      const response = await fetch(url);
      console.log(`🔍 [前端] audio状态响应: ${response.status} ${response.statusText}`);

      if (!response.ok) {
        const errorText = await response.text();
        console.error(`❌ [前端] audio状态查询失败: ${response.status} - ${errorText}`);
        throw new Error(`查询失败: ${response.status}`);
      }

      const result = await response.json();
      console.log(`✅ [前端] audio状态查询成功:`, result);
      return result;
    } catch (error) {
      console.error(`❌ [前端] audio状态查询异常:`, error);
      throw error;
    }
  };

  // 检查后端健康状态
  const checkHealthStatus = async () => {
    try {
      setHealthStatus('checking');
      setHealthError(null);

      const response = await fetch(`${API_BASE_URL}/api/health`);

      if (response.ok) {
        const healthData = await response.json();
        setHealthStatus('healthy');
        console.log('🟢 后端健康状态:', healthData);
      } else {
        setHealthStatus('unhealthy');
        setHealthError(`HTTP ${response.status}`);
        console.warn('🟡 后端响应异常:', response.status);
      }
    } catch (error) {
      setHealthStatus('error');
      setHealthError(error.message);
      console.error('🔴 后端健康检查失败:', error);
    }
  };

  // 任务类
  class SegmentTask {
    constructor(audioBlob, audioId, segmentIndex, duration) {
      this.audioBlob = audioBlob;
      this.audioId = audioId;
      this.segmentIndex = segmentIndex;
      this.duration = duration;
      this.uploadAttempted = false; // 标记是否已经尝试过上传
      this.retryCount = 0;
      this.maxRetries = 1; // 每个segment最多只能重试一次（总共最多处理2次）
      this.lastError = null;
      this.nextRetryTime = null;
      this.status = SegmentStatus.PENDING;
      this.transcriptionText = null; // 存储转写结果
      this.transcriptionData = null; // 存储完整的转写数据
      this.previousStatus = null; // 用于检测状态变化
    }

    async execute() {
      try {
        // 第一次尝试：上传并转写
        if (!this.uploadAttempted) {
          this.status = SegmentStatus.UPLOADING;
          this.updateUI();

          const result = await uploadAndTranscribe(
            this.audioBlob,
            this.audioId,
            this.segmentIndex,
            this.duration
          );

          this.uploadAttempted = true;

          // 保存转写结果（如果有的话）
          if (result.transcription_text || result.transcription_data) {
            this.transcriptionText = result.transcription_text;
            this.transcriptionData = result.transcription_data;
            console.log(`💾 片段 ${this.segmentIndex} 保存转写结果: "${this.transcriptionText}"`);
          }

          // 清理失败记录
          if (failedTasksRef.current[this.segmentIndex]) {
            delete failedTasksRef.current[this.segmentIndex];
          }

          // 无论API返回什么结果，都必须进行状态查询以确保安全性
          console.log(`🔍 片段 ${this.segmentIndex} upload_and_transcribe 完成，进行状态查询确认`);

          try {
            const statusResult = await querySegmentStatus(this.audioId, this.segmentIndex);

            if (statusResult.success) {
              if (statusResult.transcription_file_exists) {
                // 状态查询确认转写完成
                this.status = SegmentStatus.COMPLETED;
                console.log(`✅ 片段 ${this.segmentIndex} 转写完成（状态查询确认）`);

                // 从状态查询结果中获取转写数据
                if (statusResult.transcription_data) {
                  this.transcriptionText = statusResult.transcription_data.text;
                  this.transcriptionData = statusResult.transcription_data;
                  console.log(`💾 片段 ${this.segmentIndex} 从状态查询保存转写结果: "${this.transcriptionText}"`);
                }

                // 如果API也返回了转写内容，进行对比验证
                if (result.success && result.transcription_text) {
                  console.log(`📄 API返回的转写内容: "${result.transcription_text.substring(0, 100)}..."`);
                  console.log(`✅ API结果与状态查询一致，转写成功`);
                }
              } else if (statusResult.audio_file_exists) {
                // 状态查询确认仅上传成功，需要继续转写
                console.log(`📋 片段 ${this.segmentIndex} 上传成功但转写未完成（状态查询确认），开始转写`);

                try {
                  const transcribeResult = await this.transcribeOnly(statusResult.audio_file_url);

                  if (transcribeResult.success) {
                    // 保存转写结果
                    if (transcribeResult.transcription_data) {
                      this.transcriptionText = transcribeResult.transcription_data.text;
                      this.transcriptionData = transcribeResult.transcription_data;
                      console.log(`💾 片段 ${this.segmentIndex} 从transcribe_only保存转写结果: "${this.transcriptionText}"`);
                    }

                    // transcribe_only完成后再次查询状态确认
                    console.log(`🔍 片段 ${this.segmentIndex} transcribe_only 完成，再次状态查询`);

                    try {
                      const finalStatusResult = await querySegmentStatus(this.audioId, this.segmentIndex);

                      if (finalStatusResult.success && finalStatusResult.transcription_file_exists) {
                        this.status = SegmentStatus.COMPLETED;
                        console.log(`✅ 片段 ${this.segmentIndex} 后续转写完成（状态查询确认）`);

                        // 从最终状态查询结果中更新转写数据
                        if (finalStatusResult.transcription_data) {
                          this.transcriptionText = finalStatusResult.transcription_data.text;
                          this.transcriptionData = finalStatusResult.transcription_data;
                          console.log(`💾 片段 ${this.segmentIndex} 从最终状态查询更新转写结果: "${this.transcriptionText}"`);
                        }
                      } else {
                        this.status = SegmentStatus.FAILED;
                        console.log(`❌ 片段 ${this.segmentIndex} 后续转写后状态查询失败`);
                      }
                    } catch (statusError) {
                      console.error(`❌ 片段 ${this.segmentIndex} 后续转写后状态查询异常:`, statusError);
                      this.status = SegmentStatus.FAILED;
                    }
                  } else {
                    console.error(`❌ 片段 ${this.segmentIndex} 后续转写失败: ${transcribeResult.error}`);
                    this.status = SegmentStatus.FAILED;
                  }
                } catch (transcribeError) {
                  console.error(`❌ 片段 ${this.segmentIndex} 后续转写异常:`, transcribeError);
                  this.status = SegmentStatus.FAILED;
                }
              } else {
                // 状态查询确认文件都不存在
                this.status = SegmentStatus.FAILED;
                console.log(`❌ 片段 ${this.segmentIndex} 处理失败，文件都不存在（状态查询确认）`);
              }
            } else {
              this.status = SegmentStatus.FAILED;
              console.log(`❌ 片段 ${this.segmentIndex} 状态查询失败`);
            }
          } catch (statusError) {
            console.error(`❌ 片段 ${this.segmentIndex} 状态查询异常:`, statusError);

            // 状态查询失败时，基于API返回结果进行判断
            if (result.success && result.transcription_completed) {
              this.status = SegmentStatus.COMPLETED;
              console.log(`⚠️ 状态查询失败，但API显示转写完成，标记为完成`);
            } else if (result.success && result.segment_uploaded) {
              this.status = SegmentStatus.UPLOADED;
              console.log(`⚠️ 状态查询失败，但API显示上传成功，标记为仅上传`);
            } else {
              this.status = SegmentStatus.FAILED;
              console.log(`❌ 状态查询失败，API也失败，标记为失败`);
            }
          }

          this.updateUI();
          return result;
        }

        // 第二次尝试（重试）：先查询状态，根据状态决定重试方式
        if (this.retryCount === 0) {
          this.status = SegmentStatus.UPLOADING;
          this.updateUI();

          console.log(`📋 片段 ${this.segmentIndex} 首次失败，查询状态决定重试方式`);

          const statusResult = await querySegmentStatus(this.audioId, this.segmentIndex);

          if (statusResult.success) {
            if (statusResult.transcription_file_exists) {
              // 转写已经完成
              this.status = SegmentStatus.COMPLETED;
              this.updateUI();
              console.log(`✅ 片段 ${this.segmentIndex} 转写已完成（状态查询发现）`);
              return statusResult;
            } else if (statusResult.audio_file_exists) {
              // 上传成功但转写失败，仅重新转写
              console.log(`📋 片段 ${this.segmentIndex} 上传成功但转写失败，仅重新转写`);

              const transcribeResult = await this.transcribeOnly(statusResult.audio_file_url);

              if (transcribeResult.success) {
                // 保存转写结果
                if (transcribeResult.transcription_data) {
                  this.transcriptionText = transcribeResult.transcription_data.text;
                  this.transcriptionData = transcribeResult.transcription_data;
                  console.log(`💾 片段 ${this.segmentIndex} 从重试transcribe_only保存转写结果: "${this.transcriptionText}"`);
                }

                // transcribe_only完成后也要查询状态确认
                console.log(`🔍 片段 ${this.segmentIndex} transcribe_only 完成，查询最终状态`);

                try {
                  const finalStatusResult = await querySegmentStatus(this.audioId, this.segmentIndex);

                  if (finalStatusResult.success && finalStatusResult.transcription_file_exists) {
                    this.status = SegmentStatus.COMPLETED;
                    console.log(`✅ 片段 ${this.segmentIndex} 重新转写成功（状态查询确认）`);

                    // 从最终状态查询结果中更新转写数据
                    if (finalStatusResult.transcription_data) {
                      this.transcriptionText = finalStatusResult.transcription_data.text;
                      this.transcriptionData = finalStatusResult.transcription_data;
                      console.log(`💾 片段 ${this.segmentIndex} 从重试最终状态查询更新转写结果: "${this.transcriptionText}"`);
                    }
                  } else {
                    this.status = SegmentStatus.FAILED;
                    console.log(`❌ 片段 ${this.segmentIndex} 重新转写后状态查询失败`);
                  }
                } catch (statusError) {
                  console.error(`❌ 片段 ${this.segmentIndex} 重新转写后状态查询异常:`, statusError);
                  this.status = SegmentStatus.COMPLETED; // 假设成功，因为API返回success
                }

                this.updateUI();
                return transcribeResult;
              } else {
                throw new Error(`重新转写失败: ${transcribeResult.error}`);
              }
            } else {
              // 文件都不存在，重新上传并转写
              console.log(`📋 片段 ${this.segmentIndex} 文件都不存在，重新上传并转写`);

              const result = await uploadAndTranscribe(
                this.audioBlob,
                this.audioId,
                this.segmentIndex,
                this.duration
              );

              // 重新上传并转写完成后也要查询状态确认
              console.log(`🔍 片段 ${this.segmentIndex} 重新上传并转写完成，查询最终状态`);

              try {
                const finalStatusResult = await querySegmentStatus(this.audioId, this.segmentIndex);

                if (finalStatusResult.success) {
                  if (finalStatusResult.transcription_file_exists) {
                    this.status = SegmentStatus.COMPLETED;
                    console.log(`✅ 片段 ${this.segmentIndex} 重新上传并转写成功（状态查询确认）`);
                  } else if (finalStatusResult.audio_file_exists) {
                    this.status = SegmentStatus.UPLOADED;
                    console.log(`📤 片段 ${this.segmentIndex} 重新上传成功但转写未完成（状态查询确认）`);
                  } else {
                    this.status = SegmentStatus.FAILED;
                    console.log(`❌ 片段 ${this.segmentIndex} 重新处理失败，文件都不存在（状态查询确认）`);
                  }
                } else {
                  this.status = SegmentStatus.FAILED;
                  console.log(`❌ 片段 ${this.segmentIndex} 重新处理后状态查询失败`);
                }
              } catch (statusError) {
                console.error(`❌ 片段 ${this.segmentIndex} 重新处理后状态查询异常:`, statusError);
                this.status = result.transcription_completed ? SegmentStatus.COMPLETED : SegmentStatus.FAILED;
              }

              this.updateUI();
              return result;
            }
          } else {
            throw new Error(`查询状态失败: ${statusResult.error}`);
          }
        }

      } catch (error) {
        // 即使失败，也要查询状态确认实际情况
        console.log(`🔍 片段 ${this.segmentIndex} upload_and_transcribe 失败，查询实际状态`);

        let statusResult = null;
        try {
          statusResult = await querySegmentStatus(this.audioId, this.segmentIndex);

          if (statusResult.success) {
            if (statusResult.transcription_file_exists) {
              this.status = SegmentStatus.COMPLETED;
              console.log(`✅ 片段 ${this.segmentIndex} 转写已完成（尽管API失败，状态查询确认成功）`);
            } else if (statusResult.audio_file_exists) {
              this.status = SegmentStatus.UPLOADED;
              console.log(`📤 片段 ${this.segmentIndex} 上传成功但转写未完成（尽管API失败，状态查询确认）`);
            } else {
              this.status = SegmentStatus.FAILED;
              console.log(`❌ 片段 ${this.segmentIndex} 确实失败，文件都不存在（状态查询确认）`);
            }
          } else {
            this.status = SegmentStatus.FAILED;
            console.log(`❌ 片段 ${this.segmentIndex} 状态查询也失败`);
          }
        } catch (statusError) {
          console.error(`❌ 片段 ${this.segmentIndex} 失败后状态查询异常:`, statusError);
        }

        // 只有在真正失败的情况下才调用onError
        // 如果状态查询显示文件已上传，就不要标记为失败
        if (this.status !== SegmentStatus.COMPLETED && this.status !== SegmentStatus.UPLOADED) {
          this.onError(error);
          throw error;
        } else if (this.status === SegmentStatus.UPLOADED && statusResult && statusResult.audio_file_url) {
          // 如果是uploaded状态，需要继续转写
          console.log(`📋 片段 ${this.segmentIndex} 失败后发现文件已上传，开始转写`);

          try {
            const transcribeResult = await this.transcribeOnly(statusResult.audio_file_url);

            if (transcribeResult.success) {
              // 保存转写结果
              if (transcribeResult.transcription_data) {
                this.transcriptionText = transcribeResult.transcription_data.text;
                this.transcriptionData = transcribeResult.transcription_data;
                console.log(`💾 片段 ${this.segmentIndex} 从失败后transcribe_only保存转写结果: "${this.transcriptionText}"`);
              }

              // transcribe_only完成后再次查询状态确认
              console.log(`🔍 片段 ${this.segmentIndex} 转写完成，查询最终状态确认`);

              try {
                const finalStatusResult = await querySegmentStatus(this.audioId, this.segmentIndex);

                if (finalStatusResult.success && finalStatusResult.transcription_file_exists) {
                  this.status = SegmentStatus.COMPLETED;
                  console.log(`✅ 片段 ${this.segmentIndex} 转写完成（失败后状态查询+转写成功）`);

                  // 从最终状态查询结果中更新转写数据
                  if (finalStatusResult.transcription_data) {
                    this.transcriptionText = finalStatusResult.transcription_data.text;
                    this.transcriptionData = finalStatusResult.transcription_data;
                    console.log(`💾 片段 ${this.segmentIndex} 从失败后最终状态查询更新转写结果: "${this.transcriptionText}"`);
                  }

                  this.updateUI();
                  return transcribeResult;
                } else {
                  this.status = SegmentStatus.FAILED;
                  console.log(`❌ 片段 ${this.segmentIndex} 转写后状态查询失败`);
                }
              } catch (statusError) {
                console.error(`❌ 片段 ${this.segmentIndex} 转写后状态查询异常:`, statusError);
                this.status = SegmentStatus.COMPLETED; // 假设成功，因为API返回success
              }
            } else {
              console.error(`❌ 片段 ${this.segmentIndex} 转写失败: ${transcribeResult.error}`);
              this.status = SegmentStatus.FAILED;
              this.onError(error);
              throw error;
            }
          } catch (transcribeError) {
            console.error(`❌ 片段 ${this.segmentIndex} 转写异常:`, transcribeError);
            this.status = SegmentStatus.FAILED;
            this.onError(error);
            throw error;
          }
        }
      }
    }

    // 仅转写方法
    async transcribeOnly(audioFileUrl) {
      try {
        console.log(`🚀 开始仅转写片段 ${this.segmentIndex}`);
        const response = await fetch(`${API_BASE_URL}/api/transcribe_only`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            audio_id: this.audioId,
            segment_index: this.segmentIndex,
            audio_file_url: audioFileUrl
          })
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || `转写失败: ${response.status}`);
        }

        const result = await response.json();
        console.log(`📝 片段 ${this.segmentIndex} transcribe_only API返回:`, result);

        // 无论转写API返回什么结果，都必须进行状态查询确认
        console.log(`🔍 片段 ${this.segmentIndex} transcribe_only 完成，进行状态查询确认`);

        try {
          const statusResult = await querySegmentStatus(this.audioId, this.segmentIndex);

          if (statusResult.success && statusResult.transcription_file_exists) {
            console.log(`✅ 片段 ${this.segmentIndex} 转写完成（状态查询确认）`);

            // 如果API也返回了转写内容，进行验证
            if (result.success && result.transcription_text) {
              console.log(`📄 API返回的转写内容: "${result.transcription_text.substring(0, 100)}..."`);
              console.log(`✅ 转写API结果与状态查询一致`);
            }

            return {
              ...result,
              verified_by_status_query: true,
              final_status: 'completed'
            };
          } else {
            console.log(`❌ 片段 ${this.segmentIndex} 转写后状态查询失败，转写可能未成功`);
            return {
              ...result,
              verified_by_status_query: true,
              final_status: 'failed'
            };
          }
        } catch (statusError) {
          console.error(`❌ 片段 ${this.segmentIndex} 转写后状态查询异常:`, statusError);
          console.log(`⚠️ 状态查询失败，基于API返回结果判断`);
          return {
            ...result,
            verified_by_status_query: false,
            status_query_error: statusError.message
          };
        }

      } catch (error) {
        console.error(`❌ 片段 ${this.segmentIndex} 仅转写失败:`, error);

        // 即使转写API失败，也要查询状态确认实际情况
        console.log(`🔍 片段 ${this.segmentIndex} 转写失败，查询实际状态`);

        try {
          const statusResult = await querySegmentStatus(this.audioId, this.segmentIndex);

          if (statusResult.success && statusResult.transcription_file_exists) {
            console.log(`💡 片段 ${this.segmentIndex} 尽管API失败，但状态查询显示转写已完成`);
            return {
              success: true,
              transcription_successful: true,
              api_failed: true,
              error: error.message,
              final_status: 'completed'
            };
          } else if (statusResult.success && statusResult.audio_file_exists) {
            console.log(`📤 片段 ${this.segmentIndex} 尽管API失败，但状态查询显示文件已上传`);
            return {
              success: false,
              transcription_successful: false,
              api_failed: true,
              error: error.message,
              final_status: 'uploaded'
            };
          } else {
            console.log(`❌ 片段 ${this.segmentIndex} API失败，状态查询也确认失败`);
            throw error;
          }
        } catch (statusError) {
          console.error(`❌ 片段 ${this.segmentIndex} 失败后状态查询异常:`, statusError);
          throw error;
        }
      }
    }

    onError(error) {
      console.error(`❌ 片段 ${this.segmentIndex} 处理失败:`, error);

      this.lastError = error.message;
      this.status = SegmentStatus.FAILED;

      // 重试逻辑：总共最多处理2次（首次1次+重试1次）
      if (this.retryCount === 0) {
        // 第一次失败，安排重试
        this.retryCount++;
        console.log(`🔄 片段 ${this.segmentIndex} 首次失败，3秒后重试 (第${this.retryCount}/${this.maxRetries + 1}次处理)`);

        const retryDelay = 3000;
        this.nextRetryTime = Date.now() + retryDelay;

        // 安排重试
        scheduleRetry(this);
      } else {
        // 第二次失败，停止重试
        console.error(`💀 片段 ${this.segmentIndex} 处理失败 (总共${this.retryCount + 1}次)，达到最大次数，最终失败`);
        this.status = SegmentStatus.FAILED;

        // 清理重试定时器，确保不会再继续重试
        if (retryTimersRef.current[this.segmentIndex]) {
          clearTimeout(retryTimersRef.current[this.segmentIndex]);
          delete retryTimersRef.current[this.segmentIndex];
        }
      }

      this.updateUI();
    }

    updateUI() {
      // 检测状态变化
      const statusChanged = this.previousStatus !== this.status;
      this.previousStatus = this.status;

      setSegments(prev => {
        // 检查是否有本地音频缓存
        const localAudioData = audioSegmentsRef.current.get(this.segmentIndex);

        return prev.map(seg =>
          seg.index === this.segmentIndex
            ? {
                ...seg,
                status: this.status,
                retryCount: this.retryCount,
                error: this.lastError,
                nextRetryTime: this.nextRetryTime,
                uploadAttempted: this.uploadAttempted,
                // 添加转写结果
                transcription: this.transcriptionData || seg.transcription,
                // 如果还没有本地音频URL，尝试添加
                localAudioUrl: seg.localAudioUrl || localAudioData?.audioUrl || null,
                hasLocalAudio: seg.hasLocalAudio || !!localAudioData
              }
            : seg
        );
      });

      // 状态变化时的缓存管理
      if (statusChanged) {
        // 当segment完成时，安排缓存清理
        if (this.status === SegmentStatus.COMPLETED) {
          console.log(`🎯 segment ${this.segmentIndex} 已完成，安排缓存清理`);
          scheduleCacheCleanup(this.segmentIndex);
        }
      }
    }
  }

  // 安排重试
  const scheduleRetry = (task) => {
    const retryDelay = task.nextRetryTime - Date.now();

    console.log(`⏰ 安排片段 ${task.segmentIndex} 在 ${retryDelay/1000} 秒后重试`);

    const timerId = setTimeout(async () => {
      // 确保只重试一次
      if (task.retryCount === 1) {
        task.status = SegmentStatus.RETRYING;
        task.updateUI();

        try {
          await task.execute();
        } catch (err) {
          console.error(`❌ 片段 ${task.segmentIndex} 重试失败:`, err);
        }
      } else {
        console.log(`⏭️ 片段 ${task.segmentIndex} 处理次数已达到上限 (最多2次)，停止重试`);
      }

      // 清理定时器记录
      delete retryTimersRef.current[task.segmentIndex];
    }, retryDelay);

    retryTimersRef.current[task.segmentIndex] = timerId;
  };

  // 处理任务队列
  const processQueue = async () => {
    if (isProcessingQueueRef.current || taskQueueRef.current.length === 0) {
      return;
    }

    isProcessingQueueRef.current = true;
    console.log(`🔄 开始处理任务队列，剩余 ${taskQueueRef.current.length} 个任务`);

    while (taskQueueRef.current.length > 0) {
      const task = taskQueueRef.current.shift();

      try {
        await task.execute();
      } catch (error) {
        console.error(`任务执行失败:`, error);
        // 任务内部已经处理了重试逻辑
      }
    }

    isProcessingQueueRef.current = false;
    console.log('✅ 任务队列处理完成');
  };

  // 添加任务到队列
  const addTaskToQueue = (audioBlob, audioId, segmentIndex, duration) => {
    const task = new SegmentTask(audioBlob, audioId, segmentIndex, duration);
    taskQueueRef.current.push(task);

    // 立即尝试处理队列
    processQueue();
  };

  // 加载历史会话数据
  const loadHistoricalSession = async (audioId) => {
    try {
      console.log('🔍 加载历史会话数据:', audioId);

      const sessionData = await queryAudioStatus(audioId);
      console.log('📋 历史会话信息:', sessionData);

      // 转换历史片段数据格式
      const formattedSegments = sessionData.segments.map(segment => ({
        index: segment.segment_index,
        fileName: `segment_${segment.segment_index}.webm`,
        size: 0, // 历史数据中可能没有size信息
        duration: 5, // 默认5秒
        status: segment.status === 'completed' ? SegmentStatus.COMPLETED :
                segment.status === 'uploaded' ? SegmentStatus.UPLOADED :
                SegmentStatus.FAILED,
        transcription: segment.transcription_data
      }));

      setSegments(formattedSegments);
      setIsHistoricalSession(true);
      setError('');

    } catch (err) {
      console.error('❌ 加载历史会话失败:', err);
      setError('加载历史会话失败: ' + err.message);
    }
  };

  // URL参数解析和会话加载
  useEffect(() => {
    if (sessionId) {
      console.log('🔗 检测到URL中的会话ID:', sessionId);
      audioIdRef.current = sessionId;

      // 检查本地存储中是否标记为新的录音会话，并且检查页面是否是刚加载的
      const sessionKey = `new_recording_${sessionId}`;
      const isNewRecording = localStorage.getItem(sessionKey) === 'true';
      const pageLoadKey = `page_loaded_${sessionId}`;
      const hasPageLoaded = localStorage.getItem(pageLoadKey) === 'true';

      console.log(`🔍 会话检查: newRecording=${isNewRecording}, pageLoaded=${hasPageLoaded}`);

      if (isNewRecording && !hasPageLoaded) {
        console.log('🎙️ 检测到新的录音会话，准备开始录音...');
        // 对于新的录音会话，不加载历史数据，而是准备开始录音
        setIsHistoricalSession(false);

        // 标记页面已加载，防止刷新页面时重复触发
        localStorage.setItem(pageLoadKey, 'true');

        // 延迟一点时间确保页面跳转完成后再开始录音
        setTimeout(() => {
          console.log('🎙️ 开始新的录音会话...');
          // 调用实际的录音初始化逻辑
          initializeRecording();
        }, 200);
      } else {
        console.log('📋 加载历史会话数据或刷新页面...');

        // 如果页面已加载过，确保页面加载标记存在
        if (isNewRecording && hasPageLoaded) {
          console.log('🔄 检测到页面刷新，但录音已在进行中');
          localStorage.setItem(pageLoadKey, 'true');
        }

        loadHistoricalSession(sessionId);
      }
    }
  }, [sessionId]);

  // 健康状态检查 - 只在页面启动时检查一次
  useEffect(() => {
    // 页面启动时检查一次后端健康状态
    checkHealthStatus();
  }, [API_BASE_URL]);

  // 录音初始化逻辑 - 提取出来以便在不同场景下复用
  const initializeRecording = async () => {
    try {
      console.log('🎙️ 开始初始化录音...');

      // 检测浏览器类型
      const isMacChrome = /Mac/.test(navigator.userAgent) && /Chrome/.test(navigator.userAgent);
      const isChrome = /Chrome/.test(navigator.userAgent);
      console.log(`🔍 检测到浏览器: ${isMacChrome ? 'Mac Chrome' : isChrome ? 'Chrome' : '其他'}`);

      // 获取最新设备列表
      const devices = await getAudioDevices();

      // 根据浏览器和设备调整音频配置
      let audioConstraints;
      let mimeType;
      let deviceId = selectedDeviceId;

      if (isMacChrome) {
        console.log('🔧 使用Mac Chrome优化的音频配置');

        // 对于Mac Chrome，尝试多种配置以解决AirPods兼容性问题
        const possibleConfigs = [
          // 配置1: 针对蓝牙设备的优化配置
          {
            deviceId: deviceId ? { exact: deviceId } : undefined,
            echoCancellation: false,
            noiseSuppression: false,
            autoGainControl: false,
            sampleRate: 48000,
            channelCount: 1,
            latency: 0.01,
            googEchoCancellation: false,
            googNoiseSuppression: false,
            googAutoGainControl: false
          },
          // 配置2: 更保守的配置
          {
            deviceId: deviceId ? { exact: deviceId } : undefined,
            echoCancellation: false,
            noiseSuppression: true,
            autoGainControl: false,
            sampleRate: 44100,
            channelCount: 1,
            googEchoCancellation: false
          },
          // 配置3: 最基本的设置
          {
            deviceId: deviceId ? { exact: deviceId } : undefined,
            sampleRate: 44100,
            channelCount: 1
          },
          // 配置4: 完全开放的配置
          {
            deviceId: deviceId ? { exact: deviceId } : undefined
          }
        ];

        audioConstraints = possibleConfigs[0];
        mimeType = 'audio/webm'; // 不指定codecs，提高兼容性

        // 尝试获取音频流，如果失败则尝试其他配置
        let stream = null;
        let configIndex = 0;

        while (!stream && configIndex < possibleConfigs.length) {
          try {
            console.log(`🔧 尝试配置 ${configIndex + 1}/${possibleConfigs.length}:`, possibleConfigs[configIndex]);
            stream = await navigator.mediaDevices.getUserMedia({
              audio: possibleConfigs[configIndex]
            });
            console.log(`✅ 配置 ${configIndex + 1} 成功`);
            audioConstraints = possibleConfigs[configIndex];
          } catch (error) {
            console.warn(`❌ 配置 ${configIndex + 1} 失败:`, error.message);
            configIndex++;
          }
        }

        if (!stream) {
          throw new Error('所有音频配置都失败，请检查设备权限');
        }

        streamRef.current = stream;

      } else {
        console.log('🔧 使用标准音频配置');

        // 非Mac Chrome的配置
        audioConstraints = {
          deviceId: deviceId ? { exact: deviceId } : undefined,
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
          sampleRate: 44100,
          channelCount: 1
        };
        mimeType = 'audio/webm;codecs=opus';

        const stream = await navigator.mediaDevices.getUserMedia({
          audio: audioConstraints
        });
        streamRef.current = stream;
      }

      console.log('🎤 麦克风权限获取成功');

      // 获取并显示音频轨道信息
      const audioTrack = streamRef.current.getAudioTracks()[0];
      if (audioTrack) {
        const settings = audioTrack.getSettings();
        console.log(`📊 音频轨道信息:`, settings);
        console.log(`🎤 当前设备: ${settings.deviceId || '未知'}`);
        console.log(`🔊 采样率: ${settings.sampleRate}Hz`);
        console.log(`📢 声道数: ${settings.channelCount}`);

        // 检查设备标签，帮助调试
        const deviceLabel = audioTrack.label || '未知设备';
        console.log(`🏷️ 设备标签: ${deviceLabel}`);

        // 如果是AirPods，给出特别提示
        if (deviceLabel.toLowerCase().includes('airpods') || deviceLabel.toLowerCase().includes('air')) {
          console.log('🎧 检测到AirPods设备，使用优化配置');
        }
      }

      // 检查支持的MIME类型 - 优先考虑Chrome兼容性
      const supportedTypes = [
        'audio/webm',
        'audio/webm;codecs=opus',
        'audio/mp4',
        'audio/ogg'
      ];

      let finalMimeType = mimeType;
      if (MediaRecorder.isTypeSupported(mimeType)) {
        console.log(`✅ 支持的MIME类型: ${mimeType}`);
      } else {
        // 找到第一个支持的类型
        for (const type of supportedTypes) {
          if (MediaRecorder.isTypeSupported(type)) {
            finalMimeType = type;
            console.log(`🔄 切换到支持的MIME类型: ${type}`);
            break;
          }
        }
      }

      // 创建MediaRecorder - 针对不同浏览器优化
      let mediaRecorder;
      try {
        const recorderOptions = {
          mimeType: finalMimeType
        };

        // 对于Chrome，添加更多选项以提高兼容性
        if (isChrome) {
          recorderOptions.audioBitsPerSecond = 128000; // 128kbps
        }

        mediaRecorder = new MediaRecorder(streamRef.current, recorderOptions);
      } catch (error) {
        console.error('❌ MediaRecorder创建失败，尝试不使用MIME类型:', error);
        // 如果失败，尝试不指定MIME类型
        try {
          mediaRecorder = new MediaRecorder(streamRef.current);
          finalMimeType = mediaRecorder.mimeType || 'audio/webm';
        } catch (secondError) {
          console.error('❌ MediaRecorder创建完全失败:', secondError);
          throw new Error('无法创建MediaRecorder，请检查浏览器兼容性');
        }
      }
      mediaRecorderRef.current = mediaRecorder;

      console.log(`🎯 MediaRecorder创建成功，使用MIME类型: ${finalMimeType}`);
      console.log(`🎯 MediaRecorder状态: ${mediaRecorder.state}`);

      // 验证录音器是否正常工作
      if (mediaRecorder.state !== 'inactive') {
        console.warn('⚠️ MediaRecorder状态异常，可能影响录音');
      }

      // 录音数据处理 - 统一的事件处理器
      mediaRecorder.ondataavailable = (event) => {
        console.log(`📊 收到音频数据: ${event.data.size} bytes, type: ${event.data.type}`);

        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);

          // 检查数据大小是否异常
          if (event.data.size < 100) {
            console.warn('⚠️ 音频数据块过小，可能存在录音问题');
          }
        } else {
          console.warn('⚠️ 收到空的音频数据块');
        }
      };

      mediaRecorder.onstop = () => {
        createAudioSegment();
      };

      // 立即开始录音
      audioChunksRef.current = [];
      isRecordingRef.current = true;
      const now = Date.now();
      recordingStartTimeRef.current = now;
      segmentStartTimeRef.current = now;

      mediaRecorder.start();
      setIsRecording(true);
      setIsStarting(false);

      // 启动定时器
      timerRef.current = setInterval(() => {
        setRecordingTime(prev => prev + 1);
      }, 1000);

      // 针对Mac Chrome的特殊处理：使用更短的录音间隔
      if (isMacChrome) {
        console.log('🔧 Mac Chrome：使用3秒录音间隔以提高成功率');
        startSplitTimer(3); // 使用3秒间隔
      } else {
        // 启动切分定时器 - 每5秒自动切分，完全按照参考文件
        startSplitTimer();
      }

      console.log('✅ 录音启动成功');

    } catch (err) {
      console.error('录音启动失败:', err);
      setError('无法访问麦克风，请检查权限设置');
      setIsStarting(false);
    }
  };

  // 开始录音 - 完全基于 audio-segment-recorder.html 的实现
  const startRecording = async () => {
    try {
      setError('');
      setSegments([]);
      setRecordingTime(0);
      setIsHistoricalSession(false);
      segmentIndexRef.current = 1; // 从1开始，与参考文件一致
      audioChunksRef.current = [];
      audioSegmentsRef.current = new Map(); // 清理本地音频缓存
      taskQueueRef.current = [];

      setIsStarting(true);
      console.log('🚀 开始初始化录音...');

      const newAudioId = generateAudioId();
      audioIdRef.current = newAudioId;

      // 在本地存储中标记为新的录音会话
      const sessionKey = `new_recording_${newAudioId}`;
      localStorage.setItem(sessionKey, 'true');

      // 立即跳转到新的录音页面，使用简洁的 URL
      console.log(`🔗 立即跳转到录音页面: /record-test-new/${newAudioId}`);
      navigate(`/record-test-new/${newAudioId}`, { replace: true });

    } catch (err) {
      console.error('录音启动失败:', err);
      setError('无法访问麦克风，请检查权限设置');
      setIsStarting(false);
    }
  };

  // 启动切分定时器 - 支持自定义间隔
  const startSplitTimer = (customInterval = null) => {
    const interval = customInterval || uploadInterval;
    console.log(`⏰ 启动切分定时器，间隔: ${interval}秒`);

    splitTimerRef.current = setInterval(() => {
      if (isRecordingRef.current && mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
        console.log('🔪 执行音频切分');

        // 检查是否有音频数据
        const isMacChrome = /Mac/.test(navigator.userAgent) && /Chrome/.test(navigator.userAgent);
        if (isMacChrome && audioChunksRef.current.length === 0) {
          console.warn('⚠️ Mac Chrome检测到没有音频数据，强制重新开始录音');
          // 强制重新开始录音而不创建片段
          audioChunksRef.current = [];
          segmentStartTimeRef.current = Date.now();
          return;
        }

        // 停止当前录制并创建片段
        mediaRecorderRef.current.stop();

        // 立即开始新的录制
        setTimeout(() => {
          if (isRecordingRef.current && mediaRecorderRef.current) {
            // 重新设置事件处理器，确保每次重新开始录音后都能正确收集数据
            mediaRecorderRef.current.ondataavailable = (event) => {
              console.log(`📊 [新录音段] 收到音频数据: ${event.data.size} bytes, type: ${event.data.type}`);

              if (event.data.size > 0) {
                audioChunksRef.current.push(event.data);

                // 检查数据大小是否异常
                if (event.data.size < 100) {
                  console.warn('⚠️ [新录音段] 音频数据块过小，可能存在录音问题');
                }
              } else {
                console.warn('⚠️ [新录音段] 收到空的音频数据块');
              }
            };

            mediaRecorderRef.current.onstop = () => {
              createAudioSegment();
            };

            try {
              mediaRecorderRef.current.start();
              segmentStartTimeRef.current = Date.now();
              console.log('🎙️ 开始新的录音段');
            } catch (error) {
              console.error('❌ 启动新录音段失败:', error);
            }
          }
        }, 100); // 短暂延迟确保stop完成，与参考文件一致
      }
    }, interval * 1000); // 使用指定间隔切分
  };

  // 创建音频片段 - 完全按照参考文件的逻辑
  const createAudioSegment = () => {
    if (audioChunksRef.current.length === 0) {
      console.log('⚠️ 没有音频数据，跳过片段创建');
      return;
    }

    console.log(`🎵 创建音频片段 ${segmentIndexRef.current}`);
    console.log(`🔍 音频chunks数量: ${audioChunksRef.current.length}`);

    // 检查每个chunk的大小
    let totalSize = 0;
    audioChunksRef.current.forEach((chunk, index) => {
      console.log(`  Chunk ${index}: ${chunk.size} bytes, type: ${chunk.type}`);
      totalSize += chunk.size;
    });
    console.log(`📊 总音频数据大小: ${totalSize} bytes (${(totalSize/1024).toFixed(1)}KB)`);

    // 针对Mac Chrome的特殊处理
    const isMacChrome = /Mac/.test(navigator.userAgent) && /Chrome/.test(navigator.userAgent);
    let audioBlob;

    if (isMacChrome && totalSize < 5000) {
      console.warn('⚠️ 检测到Mac Chrome且音频数据过小，可能存在录音问题');
      // 尝试使用不同的MIME类型
      audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
    } else {
      audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm;codecs=opus' });
    }

    const audioUrl = URL.createObjectURL(audioBlob);
    const duration = (Date.now() - segmentStartTimeRef.current) / 1000;

    console.log(`📏 音频Blob大小: ${audioBlob.size} bytes (${(audioBlob.size/1024).toFixed(1)}KB)`);
    console.log(`⏱️ 片段时长: ${duration}秒`);

    // 检查音频Blob是否有效
    if (audioBlob.size < 1000) {
      console.warn('⚠️ 音频Blob过小，可能无法正常转写');

      // 尝试读取音频文件的前几个字节来检查
      try {
        const reader = new FileReader();
        reader.onload = (e) => {
          const arrayBuffer = e.target.result;
          const bytes = new Uint8Array(arrayBuffer.slice(0, 20));
          console.log('🔍 音频文件头部:', Array.from(bytes).map(b => b.toString(16).padStart(2, '0')).join(' '));
        };
        reader.readAsArrayBuffer(audioBlob.slice(0, 20));
      } catch (error) {
        console.error('❌ 读取音频文件头部失败:', error);
      }
    }

    // 保存到本地缓存
    const segmentData = {
      audioBlob,
      audioUrl,
      segmentIndex: segmentIndexRef.current,
      duration,
      timestamp: new Date().toISOString()
    };

    audioSegmentsRef.current.set(segmentIndexRef.current, segmentData);

    // 检查缓存大小限制
    enforceMaxCacheSize();

    // 立即在界面上显示新的片段
    const newSegment = {
      index: segmentIndexRef.current,
      fileName: `segment_${segmentIndexRef.current}.webm`,
      size: audioBlob.size,
      duration: duration,
      status: SegmentStatus.PENDING,
      retryCount: 0,
      maxRetries: 1,
      error: null,
      nextRetryTime: null,
      uploadAttempted: false,
      localAudioUrl: audioUrl,
      hasLocalAudio: true
    };

    setSegments(prev => {
      // 检查是否已存在（避免重复）
      const exists = prev.some(seg => seg.index === segmentIndexRef.current);
      if (!exists) {
        console.log(`📱 立即在界面上显示新片段 ${segmentIndexRef.current}`);
        return [...prev, newSegment];
      }
      return prev;
    });

    // 添加到上传队列
    addTaskToQueue(audioBlob, audioIdRef.current, segmentIndexRef.current, duration);

    // 清空chunks准备下一段
    audioChunksRef.current = [];
    segmentIndexRef.current++;
  };

  // 暂停录音
  const pauseRecording = async () => {
    console.log('⏸️ 暂停录音...');

    // 停止切分定时器
    if (splitTimerRef.current) {
      clearInterval(splitTimerRef.current);
      splitTimerRef.current = null;
    }

    // 停止录音器，这会触发createAudioSegment
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
      mediaRecorderRef.current.stop();
    }

    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }

    setIsPaused(true);
    console.log('✅ 录音已暂停');
  };

  // 继续录音
  const resumeRecording = () => {
    console.log('▶️ 继续录音...');

    if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'paused') {
      mediaRecorderRef.current.resume();
    } else if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'inactive') {
      // 重新开始录音前重新设置事件处理器
      mediaRecorderRef.current.ondataavailable = (event) => {
        console.log(`📊 [继续录音] 收到音频数据: ${event.data.size} bytes, type: ${event.data.type}`);

        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);

          // 检查数据大小是否异常
          if (event.data.size < 100) {
            console.warn('⚠️ [继续录音] 音频数据块过小，可能存在录音问题');
          }
        } else {
          console.warn('⚠️ [继续录音] 收到空的音频数据块');
        }
      };

      mediaRecorderRef.current.onstop = () => {
        createAudioSegment();
      };

      // 重新开始录音
      try {
        mediaRecorderRef.current.start();
        segmentStartTimeRef.current = Date.now();
        console.log('🎙️ 继续录音启动成功');
      } catch (error) {
        console.error('❌ 继续录音启动失败:', error);
      }
    }

    // 重新启动定时器
    timerRef.current = setInterval(() => {
      setRecordingTime(prev => prev + 1);
    }, 1000);

    // 重新启动切分定时器
    const isMacChrome = /Mac/.test(navigator.userAgent) && /Chrome/.test(navigator.userAgent);
    if (isMacChrome) {
      startSplitTimer(3);
    } else {
      startSplitTimer();
    }

    setIsPaused(false);
    console.log('✅ 录音已继续');
  };

  // 取消录音
  const cancelRecording = () => {
    console.log('❌ 开始取消录音...');

    // 清理本地存储中的会话标记
    if (audioIdRef.current) {
      const sessionKey = `new_recording_${audioIdRef.current}`;
      const pageLoadKey = `page_loaded_${audioIdRef.current}`;
      localStorage.removeItem(sessionKey);
      localStorage.removeItem(pageLoadKey);
      console.log('🧹 清理本地存储会话和页面加载标记');
    }

    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
    }

    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }

    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }

    if (splitTimerRef.current) {
      clearInterval(splitTimerRef.current);
      splitTimerRef.current = null;
    }

    // 清理所有定时器和任务
    Object.values(retryTimersRef.current).forEach(timerId => {
      clearTimeout(timerId);
    });
    retryTimersRef.current = {};
    taskQueueRef.current = [];

    // 清理本地音频缓存
    audioSegmentsRef.current.forEach((segmentData, segmentIndex) => {
      URL.revokeObjectURL(segmentData.audioUrl);
      // 清理定时器
      if (cacheCleanupTimersRef.current[segmentIndex]) {
        clearTimeout(cacheCleanupTimersRef.current[segmentIndex]);
        delete cacheCleanupTimersRef.current[segmentIndex];
      }
    });
    audioSegmentsRef.current = new Map();

    if (audioUrl) {
      URL.revokeObjectURL(audioUrl);
      setAudioUrl(null);
    }

    isRecordingRef.current = false;
    setIsRecording(false);
    setIsPaused(false);
    setIsStarting(false);
    setRecordingTime(0);
    setSegments([]);
    setError('');
    setIsPlaying(false);
    setIsAudioLoading(false);

    audioIdRef.current = null;
    segmentIndexRef.current = 1;
    audioChunksRef.current = [];
    mediaRecorderRef.current = null;
    segmentStartTimeRef.current = null;
    recordingStartTimeRef.current = null;

    console.log('✅ 录音已取消');
  };

  // 停止录音
  const stopRecording = async () => {
    console.log('🛑 开始停止录音流程...');
    setIsStopping(true);

    const finalRecordingTime = recordingTime;
    const finalAudioId = audioIdRef.current;
    const finalSegmentIndex = segmentIndexRef.current;

    console.log('📊 录音结束状态:');
    console.log('  - Audio ID:', finalAudioId);
    console.log('  - 录音时长:', finalRecordingTime, '秒');
    console.log('  - 当前片段索引:', finalSegmentIndex);

    // 停止切分定时器
    if (splitTimerRef.current) {
      clearInterval(splitTimerRef.current);
      splitTimerRef.current = null;
    }

    // 停止录音器，这会触发最后一个片段的创建
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
    }

    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
    }

    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }

    isRecordingRef.current = false;
    setIsRecording(false);
    setIsPaused(false);

    // 清理本地存储中的会话标记（录音已结束，现在可以重新开始了）
    if (finalAudioId) {
      const sessionKey = `new_recording_${finalAudioId}`;
      const pageLoadKey = `page_loaded_${finalAudioId}`;
      localStorage.removeItem(sessionKey);
      localStorage.removeItem(pageLoadKey);
      console.log('🧹 清理本地存储会话和页面加载标记');
    }

    // 等待所有任务完成
    console.log('⏳ 等待所有任务完成...');
    await waitForAllTasksComplete();

    // 跳转到历史会话页面
    console.log('🔗 跳转到历史会话页面:', finalAudioId);
    navigate(`/record-test-new/${finalAudioId}`, { replace: true });
    setIsStopping(false);
  };

  // 等待所有任务完成
  const waitForAllTasksComplete = async () => {
    let attempts = 0;
    const maxAttempts = 30; // 最多等待30秒

    while (attempts < maxAttempts) {
      const queueEmpty = taskQueueRef.current.length === 0;
      const noRetries = Object.keys(retryTimersRef.current).length === 0;

      if (queueEmpty && noRetries) {
        console.log('✅ 所有任务已完成');
        return;
      }

      await new Promise(resolve => setTimeout(resolve, 1000));
      attempts++;
    }

    console.warn('⚠️ 强制完成等待，可能还有未完成的任务');
  };

  // 获取片段状态图标
  const getSegmentStatusIcon = (segment) => {
    switch (segment.status) {
      case SegmentStatus.COMPLETED:
        return '✅';
      case SegmentStatus.UPLOADED:
        return '⬆️';
      case SegmentStatus.UPLOADING:
      case SegmentStatus.TRANSCRIBING:
        return '🔄';
      case SegmentStatus.RETRYING:
        return '🔄';
      case SegmentStatus.FAILED:
        return '❌';
      default:
        return '⏳';
    }
  };

  // 获取片段状态文本
  const getSegmentStatusText = (segment) => {
    switch (segment.status) {
      case SegmentStatus.COMPLETED:
        return '转写完成';
      case SegmentStatus.UPLOADED:
        return '已上传';
      case SegmentStatus.UPLOADING:
        return segment.uploadAttempted ? '查询状态中...' : '上传中...';
      case SegmentStatus.TRANSCRIBING:
        return '转写中...';
      case SegmentStatus.RETRYING:
        return `重试中 (第${segment.retryCount}/${segment.maxRetries}次)`;
      case SegmentStatus.FAILED:
        if (segment.uploadAttempted) {
          return '上传失败，请检查网络';
        }
        if (segment.retryCount >= segment.maxRetries) {
          return `重试失败: ${segment.error || '已达最大重试次数'}`;
        }
        if (segment.nextRetryTime) {
          const waitTime = Math.ceil((segment.nextRetryTime - Date.now()) / 1000);
          return waitTime > 0 ? `${waitTime}秒后重试` : '准备重试';
        }
        return `失败: ${segment.error || '未知错误'}`;
      default:
        return '等待处理';
    }
  };

  // 播放本地音频
  const playLocalAudio = (segmentIndex) => {
    const audioData = audioSegmentsRef.current.get(segmentIndex);
    if (audioData && audioData.audioUrl) {
      const audio = new Audio(audioData.audioUrl);

      // 设置播放状态跟踪
      currentlyPlayingRef.current = segmentIndex;
      console.log(`▶️ 开始播放本地音频 segment ${segmentIndex}`);

      audio.addEventListener('ended', () => {
        currentlyPlayingRef.current = null;
        console.log(`⏹️ segment ${segmentIndex} 播放结束`);

        // 播放结束后检查是否需要清理
        if (cacheConfig.cleanupOnCompleted) {
          scheduleCacheCleanup(segmentIndex);
        }
      });

      audio.addEventListener('error', () => {
        currentlyPlayingRef.current = null;
        console.error(`❌ segment ${segmentIndex} 播放出错`);
      });

      audio.play().catch(err => {
        currentlyPlayingRef.current = null;
        console.error(`播放片段 ${segmentIndex} 本地音频失败:`, err);
      });
    } else {
      console.warn(`片段 ${segmentIndex} 没有本地音频数据`);
    }
  };

  // 播放远程音频
  const playRemoteAudio = async (audioId, segmentIndex) => {
    try {
      // 构建远程音频URL，这里假设后端提供了音频访问接口
      const remoteAudioUrl = `${API_BASE_URL}/api/audio/${audioId}/segment/${segmentIndex}/play`;

      const audio = new Audio(remoteAudioUrl);
      audio.play().catch(err => {
        console.error(`播放片段 ${segmentIndex} 远程音频失败:`, err);
        alert(`播放远程音频失败: ${err.message}`);
      });
    } catch (error) {
      console.error(`获取片段 ${segmentIndex} 远程音频URL失败:`, error);
      alert(`获取远程音频失败: ${error.message}`);
    }
  };

  // 手动清理所有缓存
  const clearAllCache = () => {
    console.log('🧹 手动清理所有本地缓存');

    audioSegmentsRef.current.forEach((segmentData, segmentIndex) => {
      if (segmentData.audioUrl) {
        URL.revokeObjectURL(segmentData.audioUrl);
        console.log(`🗑️ 已释放segment ${segmentIndex}的Blob URL`);
      }
    });

    audioSegmentsRef.current = new Map();

    // 清理所有定时器
    Object.values(cacheCleanupTimersRef.current).forEach(timerId => {
      clearTimeout(timerId);
    });
    cacheCleanupTimersRef.current = {};

    // 更新UI
    setSegments(prev => prev.map(seg => ({
      ...seg,
      hasLocalAudio: false,
      localAudioUrl: null
    })));

    console.log('✅ 所有本地缓存已清理');
  };

  // 下载音频片段
  const downloadSegment = (segmentIndex, isLocal = true) => {
    let url, filename;

    if (isLocal) {
      const audioData = audioSegmentsRef.current.get(segmentIndex);
      if (!audioData || !audioData.audioUrl) {
        console.warn(`片段 ${segmentIndex} 没有本地音频数据`);
        return;
      }
      url = audioData.audioUrl;
      filename = `segment_${segmentIndex}_local.webm`;
    } else {
      // 下载远程音频
      const audioId = audioIdRef.current || sessionId;
      url = `${API_BASE_URL}/api/audio/${audioId}/segment/${segmentIndex}/download`;
      filename = `segment_${segmentIndex}_remote.webm`;
    }

    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  // 清理资源
  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
      if (splitTimerRef.current) {
        clearInterval(splitTimerRef.current);
      }
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
      }
      if (audioUrl) {
        URL.revokeObjectURL(audioUrl);
      }

      // 清理本地音频缓存
      audioSegmentsRef.current.forEach((segmentData, segmentIndex) => {
        URL.revokeObjectURL(segmentData.audioUrl);
        // 清理定时器
        if (cacheCleanupTimersRef.current[segmentIndex]) {
          clearTimeout(cacheCleanupTimersRef.current[segmentIndex]);
          delete cacheCleanupTimersRef.current[segmentIndex];
        }
      });
      audioSegmentsRef.current = new Map();

      // 清理定时器
      Object.values(retryTimersRef.current).forEach(timerId => {
        clearTimeout(timerId);
      });
      Object.values(cacheCleanupTimersRef.current).forEach(timerId => {
        clearTimeout(timerId);
      });
      cacheCleanupTimersRef.current = {};
    };
  }, []);

  return (
    <div className="min-h-screen bg-black text-white">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-8 max-w-7xl">
        {/* 标题 */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold mb-2">
            {isHistoricalSession ? '历史录音查看' : (isRecording ? '正在录音' : '录音测试 (修复版)')}
          </h1>
          <p className="text-gray-400">
            {isHistoricalSession ? '查看历史录音会话' : '基于audio-segment-recorder.html的音频分段录制系统'}
          </p>
          {isHistoricalSession && (
            <div className="mt-2">
              <button
                onClick={() => {
                  navigate('/record-test-new', { replace: true });
                  setIsHistoricalSession(false);
                  setSegments([]);
                  setAudioUrl(null);
                }}
                className="px-4 py-2 bg-blue-600 rounded-full text-white text-sm hover:bg-blue-700 transition-colors"
              >
                🎤 开始新录音
              </button>
            </div>
          )}
          <div className="flex items-center justify-center mt-2 space-x-4">
            <p className="text-gray-500 text-xs">API: {API_BASE_URL}</p>
            <div className="flex items-center space-x-2">
              {/* 健康状态指示器 */}
              {healthStatus === 'checking' && (
                <>
                  <div className="animate-spin rounded-full h-2 w-2 border border-yellow-400 border-t-transparent"></div>
                  <span className="text-yellow-400 text-xs">检查中</span>
                </>
              )}
              {healthStatus === 'healthy' && (
                <>
                  <div className="w-2 h-2 bg-green-400 rounded-full"></div>
                  <span className="text-green-400 text-xs">正常</span>
                </>
              )}
              {healthStatus === 'unhealthy' && (
                <>
                  <div className="w-2 h-2 bg-yellow-400 rounded-full"></div>
                  <span className="text-yellow-400 text-xs">异常</span>
                </>
              )}
              {healthStatus === 'error' && (
                <>
                  <div className="w-2 h-2 bg-red-400 rounded-full"></div>
                  <span className="text-red-400 text-xs">离线</span>
                </>
              )}

              {/* 手动刷新按钮 */}
              <button
                onClick={checkHealthStatus}
                className="text-gray-400 hover:text-white transition-colors p-0.5 rounded hover:bg-gray-700"
                title="刷新健康状态"
              >
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
              </button>
            </div>
          </div>

          {/* 健康状态详情 */}
          {(healthStatus === 'unhealthy' || healthStatus === 'error') && (
            <div className={`mt-2 p-2 rounded text-xs ${
              healthStatus === 'unhealthy' ? 'bg-yellow-900 bg-opacity-30 text-yellow-300' :
              'bg-red-900 bg-opacity-30 text-red-300'
            }`}>
              <p>后端服务状态异常，可能影响录音和转写功能。</p>
              <p>错误信息: {healthError}</p>
              <p>请检查backend-workers服务是否正常运行。</p>
            </div>
          )}
        </div>

        {/* 错误提示 */}
        {error && (
          <div className="w-full max-w-4xl mx-auto mb-6 p-4 bg-red-900 bg-opacity-50 border border-red-600 rounded-lg">
            <div className="text-red-200 text-center text-sm">{error}</div>
          </div>
        )}

        {/* 主要内容 */}
        <div className="max-w-4xl mx-auto space-y-6">

          {/* 录音控制 */}
          {!isHistoricalSession && (
            <div className="bg-gray-900 rounded-lg p-6 text-center">
              {/* 设备选择器 */}
              {!isRecording && !isStarting && !isStopping && (
                <div className="mb-4 p-3 bg-gray-800 rounded-lg text-left">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm text-gray-400">录音设备选择</span>
                    <button
                      onClick={() => setShowDeviceSelector(!showDeviceSelector)}
                      className="text-xs text-blue-400 hover:text-blue-300 transition-colors"
                    >
                      {showDeviceSelector ? '隐藏' : '显示'}
                    </button>
                  </div>

                  {showDeviceSelector && (
                    <div className="space-y-2">
                      <div className="text-xs text-gray-500 mb-2">
                        选择录音设备可解决Chrome与AirPods兼容性问题
                      </div>
                      {availableDevices.length > 0 ? (
                        <div className="space-y-1">
                          {availableDevices.map((device) => (
                            <button
                              key={device.deviceId}
                              onClick={() => selectDevice(device.deviceId)}
                              className={`w-full text-left px-3 py-2 rounded text-xs transition-colors ${
                                selectedDeviceId === device.deviceId
                                  ? 'bg-blue-600 text-white'
                                  : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                              }`}
                            >
                              <div className="font-medium truncate">
                                {device.label || `设备 ${device.deviceId.slice(0, 8)}...`}
                              </div>
                              <div className="text-xs opacity-75 mt-1">
                                {device.label?.toLowerCase().includes('airpods') && '🎧 '}
                                {device.label?.toLowerCase().includes('builtin') && '🖥️ '}
                                {device.label?.toLowerCase().includes('external') && '🔌 '}
                                默认设备
                              </div>
                            </button>
                          ))}
                          <button
                            onClick={() => selectDevice('')}
                            className={`w-full text-left px-3 py-2 rounded text-xs transition-colors ${
                              !selectedDeviceId
                                ? 'bg-blue-600 text-white'
                                : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                            }`}
                          >
                            <div className="font-medium">
                              系统默认设备
                            </div>
                            <div className="text-xs opacity-75 mt-1">
                              推荐选项
                            </div>
                          </button>
                        </div>
                      ) : (
                        <div className="text-xs text-gray-500 p-2 bg-gray-700 rounded">
                          未检测到音频设备，请检查设备连接
                        </div>
                      )}

                      <button
                        onClick={getAudioDevices}
                        className="w-full px-3 py-2 bg-gray-700 text-gray-300 rounded text-xs hover:bg-gray-600 transition-colors"
                      >
                        🔄 刷新设备列表
                      </button>
                    </div>
                  )}

                  {selectedDeviceId && (
                    <div className="mt-2 text-xs text-green-400">
                      已选择设备: {availableDevices.find(d => d.deviceId === selectedDeviceId)?.label || '自定义设备'}
                    </div>
                  )}
                </div>
              )}

              {!isRecording && !isStarting && !isStopping ? (
                <button
                  onClick={startRecording}
                  className="px-8 py-4 bg-green-600 rounded-full text-white font-medium hover:bg-green-700 transition-colors"
                >
                  🎤 开始录音
                </button>
              ) : isStarting ? (
                <div className="space-y-4">
                  <div className="text-2xl font-mono">启动中...</div>
                  <div className="flex justify-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-500"></div>
                  </div>
                  <div className="text-sm text-gray-400">正在初始化录音功能</div>
                </div>
              ) : isStopping ? (
                <div className="space-y-4">
                  <div className="text-2xl font-mono">停止中...</div>
                  <div className="flex justify-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-500"></div>
                  </div>
                  <div className="text-sm text-gray-400">正在等待所有任务完成</div>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="text-2xl font-mono">{formatTime(recordingTime)}</div>
                  <div className="flex justify-center space-x-4">
                    {!isPaused ? (
                      <>
                        <button
                          onClick={cancelRecording}
                          className="px-6 py-3 bg-gray-600 rounded-full text-white font-medium hover:bg-gray-700 transition-colors"
                        >
                          ❌ 取消
                        </button>
                        <button
                          onClick={pauseRecording}
                          className="px-6 py-3 bg-yellow-600 rounded-full text-white font-medium hover:bg-yellow-700 transition-colors"
                        >
                          ⏸️ 暂停
                        </button>
                        <button
                          onClick={stopRecording}
                          className="px-6 py-3 bg-red-600 rounded-full text-white font-medium hover:bg-red-700 transition-colors"
                        >
                          ⏹️ 停止录音
                        </button>
                      </>
                    ) : (
                      <>
                        <button
                          onClick={cancelRecording}
                          className="px-6 py-3 bg-gray-600 rounded-full text-white font-medium hover:bg-gray-700 transition-colors"
                        >
                          ❌ 取消
                        </button>
                        <button
                          onClick={resumeRecording}
                          className="px-6 py-3 bg-blue-600 rounded-full text-white font-medium hover:bg-blue-700 transition-colors"
                        >
                          ▶️ 继续
                        </button>
                        <button
                          onClick={stopRecording}
                          className="px-6 py-3 bg-red-600 rounded-full text-white font-medium hover:bg-red-700 transition-colors"
                        >
                          ⏹️ 停止录音
                        </button>
                      </>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* 录音信息 */}
          {(isRecording || isHistoricalSession) && (
            <div className="bg-gray-900 rounded-lg p-6">
              <h2 className="text-lg font-bold mb-4">
                {isHistoricalSession ? '会话详情' : '录音信息'}
              </h2>
              <div className="grid grid-cols-1 gap-4 text-sm border border-gray-700 rounded-lg p-4 bg-gray-800">
                <div>
                  <div className="flex items-center justify-between">
                    <div className="text-gray-400">录音会话ID</div>
                    <button
                      onClick={copySessionId}
                      className="text-gray-400 hover:text-white transition-colors p-1 rounded hover:bg-gray-700"
                      title="复制录音会话ID"
                    >
                      {copySuccess ? (
                        <svg className="w-4 h-4 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                      ) : (
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                        </svg>
                      )}
                    </button>
                  </div>
                  <div className="font-mono text-xs break-all mt-1">
                    {audioIdRef.current || sessionId}
                  </div>
                  {copySuccess && (
                    <div className="text-green-400 text-xs mt-1">
                      ✓ 已复制到剪贴板
                    </div>
                  )}
                </div>
                <div>
                  <div className="text-gray-400">录音时长</div>
                  <div className="font-mono">
                    {isRecording ? formatTime(recordingTime) : '历史会话'}
                  </div>
                </div>
                <div>
                  <div className="text-gray-400">任务队列状态</div>
                  <div className="font-mono">
                    队列: {taskQueueRef.current?.length || 0} | 重试: {Object.keys(retryTimersRef.current).length}
                  </div>
                </div>
                <div>
                  <div className="text-gray-400">当前片段索引</div>
                  <div className="font-mono">
                    {segmentIndexRef.current} (从1开始)
                  </div>
                </div>
                <div>
                  <div className="text-gray-400">本地缓存状态</div>
                  <div className="flex items-center space-x-2">
                    <div className="font-mono">
                      {audioSegmentsRef.current.size} 个片段 / 最大 {cacheConfig.maxCacheSize} 个
                    </div>
                    {audioSegmentsRef.current.size > 0 && (
                      <button
                        onClick={clearAllCache}
                        className="px-2 py-1 bg-red-600 rounded text-xs text-white hover:bg-red-700 transition-colors"
                        title="清理所有本地缓存"
                      >
                        清理
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* 录音片段列表 */}
          {segments.length > 0 && (
            <div className="bg-gray-900 rounded-lg p-6">
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-4 gap-4">
                <h2 className="text-lg font-bold">
                  {isHistoricalSession ? '历史片段' : '录音片段'}
                </h2>
                <div className="text-sm text-gray-400">
                  共 {segments.length} 个片段
                </div>
              </div>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                {segments.map((segment, index) => (
                  <div key={index} className={`bg-gray-800 rounded p-3 flex justify-between items-center ${
                    segment.status === SegmentStatus.UPLOADING || segment.status === SegmentStatus.TRANSCRIBING || segment.status === SegmentStatus.RETRYING ? 'border border-blue-500' :
                    segment.status === SegmentStatus.FAILED ? 'border border-red-500' :
                    segment.status === SegmentStatus.COMPLETED ? 'border border-green-500' : ''
                  }`}>
                    <div className="flex items-center space-x-3 flex-1 min-w-0">
                      <div className="flex-shrink-0">
                        <span className="text-lg">
                          {getSegmentStatusIcon(segment)}
                        </span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-sm truncate">{segment.fileName}</div>
                        <div className="text-xs text-gray-400">
                          {segment.duration}秒 | {segment.size ? `${(segment.size / 1024).toFixed(1)}KB` : 'N/A'}
                        </div>
                        <div className="text-xs text-gray-500 mt-1">
                          {getSegmentStatusText(segment)}
                        </div>
                        {segment.transcription && (
                          <div className="text-xs text-green-400 mt-1 max-w-xs truncate">
                            "{segment.transcription.text}"
                          </div>
                        )}
                      </div>
                    </div>

                    {/* 播放和下载按钮 */}
                    <div className="flex flex-col space-y-1 ml-2">
                      <div className="flex space-x-1">
                        {/* 本地播放按钮 */}
                        {segment.hasLocalAudio && (
                          <button
                            onClick={() => playLocalAudio(segment.index)}
                            className="p-1 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
                            title="播放本地音频"
                          >
                            <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                              <path d="M5 4v12l10-6z"/>
                            </svg>
                          </button>
                        )}

                        {/* 远程播放按钮 */}
                        <button
                          onClick={() => playRemoteAudio(audioIdRef.current || sessionId, segment.index)}
                          className="p-1 bg-green-600 text-white rounded hover:bg-green-700 transition-colors"
                          title="播放远程音频"
                        >
                          <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                            <path d="M5 4v12l10-6z"/>
                          </svg>
                        </button>

                        {/* 下载按钮 */}
                        <button
                          onClick={() => downloadSegment(segment.index, segment.hasLocalAudio)}
                          className="p-1 bg-orange-600 text-white rounded hover:bg-orange-700 transition-colors"
                          title="下载音频"
                        >
                          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                          </svg>
                        </button>
                      </div>

                      {/* 按钮提示 */}
                      <div className="text-xs text-gray-500 text-center">
                        {segment.hasLocalAudio ?
                          (segment.status === SegmentStatus.COMPLETED ? '本地(待清理) | 远程' : '本地 | 远程')
                          : '仅远程'}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* 设置区域 */}
          {!isHistoricalSession && (
            <div className="bg-gray-900 rounded-lg p-4 text-sm text-gray-400">
              <div className="space-y-1">
                <p>• 点击"开始录音"开始录制</p>
                <p>• 每{uploadInterval}秒自动生成一个音频片段并上传转写</p>
                <p>• 点击"暂停"完成当前片段并暂停录音</p>
                <p>• 点击"继续"恢复录制</p>
                <p>• 点击"停止录音"结束录制并等待所有任务完成</p>
                <p>• 系统自动处理失败重试，无需手动干预</p>
                <p>• 停止录音后页面URL会包含UUID格式的音频ID，可分享或收藏</p>
                <p className="text-blue-400">• 每个片段都提供本地和远程播放按钮，便于调试</p>
                <p className="text-blue-400">• 本地音频缓存在浏览器中，可随时播放验证录音质量</p>
                <p className="text-green-400">• 修复版完全基于audio-segment-recorder.html的音频切分逻辑</p>
                <p className="text-green-400">• 确保每个切分后的音频片段都可以正常播放</p>
                <p className="text-yellow-400">• 智能缓存管理：转写完成后自动清理本地缓存以节省内存</p>
                <p className="text-yellow-400">• 正在播放的音频不会被清理，播放结束后自动处理</p>
                <p className="text-red-400">• Chrome + AirPods 兼容性修复：可手动选择录音设备解决兼容问题</p>
                <p className="text-red-400">• 如遇录音问题，请尝试：1)选择系统默认设备 2)刷新设备列表 3)使用Safari浏览器</p>
                <p className="text-purple-400">• 设备选择器支持实时切换，无需重启页面</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default RecordTestPageNewFixed;